import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import PencilIcon from '../../../icons/PencilIcon'
import {
  getInvoiceStatusBadgeStyle,
  isInvoicePaid,
} from '../../../utils/invoiceStatus'

dayjs.extend(utc)

const fmt = (n, currency = 'EUR') =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(n)

// Las fechas llegan como medianoche UTC (…T00:00:00.000Z); se formatean en UTC
// para evitar que la zona horaria local las corra al día anterior.
const fmtDate = (value) => (value ? dayjs.utc(value).format('DD/MM/YYYY') : '—')

const StatusBadge = ({ invoice }) => {
  const style = getInvoiceStatusBadgeStyle(invoice)
  const label = invoice.statusName || '—'

  return (
    <span className="d-inline-flex align-items-center gap-1 flex-wrap justify-content-center">
      <span
        className="px-2 py-1 rounded"
        style={{
          ...style,
          fontSize: '12px',
          fontWeight: '600',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: '11px',
          fontWeight: '600',
          color: invoice.accept ? '#166534' : '#991b1b',
        }}
      >
        ({invoice.accept ? 'Aceptada' : 'No aceptada'})
      </span>
    </span>
  )
}

/**
 * InvoiceTable
 *
 * Props:
 *   invoices — array de facturas:
 *     { id, created_at, due_date, description, total_amount, statusName, statusId, companyName?, currencyCode? }
 *   isLoading — boolean
 *   showCompany — boolean, muestra columna Empresa
 *   onRowClick — función, al hacer click en una fila recibe la factura
 */
const InvoiceTable = ({ invoices = [], isLoading = false, onEdit, showCompany = false, onRowClick }) => {
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border spinner-border-sm text-secondary" role="status" />
      </div>
    )
  }

  if (!invoices.length) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center gap-3 py-5 rounded"
        style={{ background: '#fafafa', border: '2px dashed #e5e7eb', color: '#9ca3af' }}
      >
        <svg
          width="36"
          height="36"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="1.2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
        </svg>
        <p className="mb-0" style={{ fontSize: '14px' }}>
          No hay facturas registradas todavía.
        </p>
      </div>
    )
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0" style={{ fontSize: '13px' }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <th
              className="py-3 px-3"
              style={{ fontWeight: '700', color: '#374151', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none' }}
            >
              NRO
            </th>
            <th
              className="py-3 px-3"
              style={{ fontWeight: '700', color: '#374151', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none' }}
            >
              Fecha emisión
            </th>
            <th
              className="py-3 px-3"
              style={{ fontWeight: '700', color: '#374151', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none' }}
            >
              Vencimiento
            </th>
            {showCompany && (
              <th
                className="py-3 px-3"
                style={{ fontWeight: '700', color: '#374151', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none' }}
              >
                Empresa
              </th>
            )}
            <th
              className="py-3 px-3"
              style={{ fontWeight: '700', color: '#374151', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none' }}
            >
              Descripción
            </th>
            <th
              className="py-3 px-3 text-end"
              style={{ fontWeight: '700', color: '#374151', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none' }}
            >
              Importe
            </th>
            <th
              className="py-3 px-3 text-center"
              style={{ fontWeight: '700', color: '#374151', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none' }}
            >
              Estado
            </th>
            {onEdit && (
              <th className="py-3 px-3" style={{ border: 'none', width: '40px' }} />
            )}
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => {
            const overdue =
              !isInvoicePaid(inv) &&
              dayjs.utc(inv.due_date).isBefore(dayjs.utc(), 'day')

            return (
              <tr
                key={inv.id}
                onClick={onRowClick ? () => onRowClick(inv) : undefined}
                style={{
                  borderBottom: '1px solid #f3f4f6',
                  cursor: onRowClick ? 'pointer' : 'default',
                }}
              >
                <td className="px-3 py-3" style={{ color: '#374151', fontFamily: 'monospace', fontSize: '12px' }}>
                  {inv.invoiceNumber || '—'}
                </td>
                <td className="px-3 py-3" style={{ color: '#374151' }}>
                  {fmtDate(inv.created_at)}
                </td>
                <td className="px-3 py-3">
                  <span style={{ color: overdue ? '#dc2626' : '#374151', fontWeight: overdue ? '600' : '400' }}>
                    {fmtDate(inv.due_date)}
                    {overdue && (
                      <span className="ms-1" style={{ fontSize: '11px', color: '#dc2626' }}>
                        · Vencida
                      </span>
                    )}
                  </span>
                </td>
                {showCompany && (
                  <td className="px-3 py-3" style={{ color: '#374151', maxWidth: '180px' }}>
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                      title={inv.companyName}
                    >
                      {inv.companyName || '—'}
                    </span>
                  </td>
                )}
                <td className="px-3 py-3" style={{ color: '#374151', maxWidth: '220px' }}>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                    }}
                    title={inv.description}
                  >
                    {inv.description || '—'}
                  </span>
                </td>
                <td className="px-3 py-3 text-end" style={{ fontWeight: '600', color: '#111827' }}>
                  {fmt(inv.total_amount, inv.currencyCode || 'EUR')}
                </td>
                <td className="px-3 py-3 text-center">
                  <StatusBadge invoice={inv} />
                </td>
                {onEdit && (
                  <td className="px-3 py-3 text-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onEdit(inv)
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
                      title="Editar factura"
                    >
                      <PencilIcon width={15} height={15} fill="#6b7280" />
                    </button>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default InvoiceTable
